import React, { useState, useEffect } from "react";
import { Table, Tag, Dropdown, Menu, Modal, Tabs, Popconfirm, message, Spin } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import moment from "moment";
import { getIngestionHistory, refreshDocument, archiveDocument, restoreDocument } from "../../../services/polly/documentService";

const { TabPane } = Tabs;

const TYPE_LABELS = {
  web: "Web",
  upload: "Upload",
  text: "Text",
  video: "Video",
};

const TYPE_COLORS = {
  web: "blue",
  upload: "purple",
  text: "default",
  video: "orange",
};

/* ── Detail modal ─────────────────────────────────────────────── */

function DetailModal({ visible, doc, onClose, onRefresh }) {
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (visible && doc) {
      setHistoryLoading(true);
      getIngestionHistory(doc.id)
        .then((res) => setHistory(Array.isArray(res) ? res : []))
        .catch(() => setHistory([]))
        .finally(() => setHistoryLoading(false));
    } else {
      setHistory([]);
    }
  }, [visible, doc]);

  if (!doc) return null;
  const isRefreshable = doc.type === "web" || doc.type === "video";

  return (
    <Modal
      title={doc.name}
      open={visible}
      onCancel={onClose}
      width={620}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            {isRefreshable && (
              <button className="ant-btn" onClick={async () => {
                const hide = message.loading("Refreshing...", 0);
                try {
                  await refreshDocument(doc.id);
                  hide();
                  message.success("Document refreshed");
                  onClose();
                  onRefresh && onRefresh();
                } catch (err) {
                  hide();
                  const errData = err?.response?.data;
                  if (errData?.message) {
                    const toast = `Refresh failed \u2014 ${errData.message}${errData.nextStep ? ". " + errData.nextStep : ""}`;
                    message.error(toast);
                  } else {
                    message.error("Refresh failed");
                  }
                }
              }}>
                Refresh now
              </button>
            )}
          </div>
          <button className="ant-btn" onClick={onClose}>Close</button>
        </div>
      }
    >
      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <div className="doc-detail-grid">
            <div className="doc-detail-cell">
              <div className="doc-detail-label">Type</div>
              <div className="doc-detail-value">
                <Tag color={TYPE_COLORS[doc.type]}>{TYPE_LABELS[doc.type] || doc.type}</Tag>
              </div>
            </div>
            <div className="doc-detail-cell">
              <div className="doc-detail-label">Status</div>
              <div className="doc-detail-value">
                <Tag color={doc.status === "active" ? "green" : "default"}>
                  {doc.status === "active" ? "Active" : "Archived"}
                </Tag>
              </div>
            </div>
            <div className="doc-detail-cell">
              <div className="doc-detail-label">Source</div>
              <div className="doc-detail-value" style={{ fontSize: 12, wordBreak: "break-all" }}>
                {doc.url || "-"}
              </div>
            </div>
            <div className="doc-detail-cell">
              <div className="doc-detail-label">Chunks in Vault</div>
              <div className="doc-detail-value">{doc.chunks}</div>
            </div>
            <div className="doc-detail-cell">
              <div className="doc-detail-label">Version</div>
              <div className="doc-detail-value">{doc.version || 1}</div>
            </div>
            <div className="doc-detail-cell">
              <div className="doc-detail-label">Added to Vault</div>
              <div className="doc-detail-value">
                {doc.lastIngested ? moment(doc.lastIngested).format("MMM D, YYYY") : "-"}
              </div>
            </div>
          </div>
        </TabPane>

        <TabPane tab="Ingestion history" key="history">
          <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
            Most recent ingestion runs for this source
          </div>
          {historyLoading ? (
            <Spin size="small" />
          ) : history.length > 0 ? (
            history.map((entry, i) => (
              <div className="doc-ingest-row" key={i}>
                <div className={`doc-ingest-dot ${entry.status === "active" ? "doc-ingest-ok" : "doc-ingest-archived"}`} />
                <div>
                  <div className="doc-ingest-label">{entry.label}</div>
                  <div className="doc-ingest-meta">
                    {entry.ingestedAt ? moment(entry.ingestedAt).format("MMM D, YYYY h:mm A") : "-"}
                    {entry.status === "archived" && <span> &middot; Archived</span>}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ color: "#999", fontSize: 13 }}>No ingestion history yet</div>
          )}
        </TabPane>

        <TabPane tab="Usage" key="usage">
          <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
            How often Polly retrieves content from this document when answering questions
          </div>
          {doc.hitRate != null ? (
            <div className="doc-detail-grid">
              <div className="doc-detail-cell">
                <div className="doc-detail-label">Vault hit rate</div>
                <div className="doc-detail-value" style={{ fontSize: 20, color: "#124173" }}>
                  {doc.hitRate}%
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                  of questions trigger retrieval from this source
                </div>
              </div>
              <div className="doc-detail-cell">
                <div className="doc-detail-label">Retrieval count (30d)</div>
                <div className="doc-detail-value" style={{ fontSize: 20, color: "#124173" }}>
                  {Math.round(doc.hitRate * 14)}
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                  times retrieved in last 30 days
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: "#999", fontSize: 13 }}>No usage data available yet</div>
          )}
        </TabPane>
      </Tabs>
    </Modal>
  );
}

/* ── Hit rate bar ─────────────────────────────────────────────── */

function HitRateBar({ rate }) {
  if (rate == null) return <span style={{ color: "#999" }}>-</span>;
  return (
    <div>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>{rate}%</div>
      <div className="doc-mgmt-hit-bar-bg">
        <div className="doc-mgmt-hit-bar-fill" style={{ width: `${Math.min(100, rate)}%` }} />
      </div>
    </div>
  );
}

/* ── URL confirm link ─────────────────────────────────────────── */

function UrlLink({ url }) {
  if (!url) return null;
  const domain = url.replace(/^https?:\/\//, "").split("/")[0];
  return (
    <Popconfirm
      title={`Open "${domain}" in a new tab?`}
      onConfirm={() => window.open(url, "_blank")}
      okText="Open"
      cancelText="Cancel"
      placement="bottomLeft"
    >
      <span className="doc-mgmt-doc-url-text">{url}</span>
    </Popconfirm>
  );
}

/* ── Table ────────────────────────────────────────────────────── */

export default function DocumentTable({ documents, total, page, pageSize, loading, onPageChange, onRefresh }) {
  const [detailDoc, setDetailDoc] = useState(null);

  const handleRefresh = async (record) => {
    const hide = message.loading("Refreshing " + record.name + "...", 0);
    try {
      const res = await refreshDocument(record.id);
      hide();
      message.success(typeof res === 'string' ? res : "Document refreshed");
      onRefresh && onRefresh();
    } catch (err) {
      hide();
      const errData = err?.response?.data;
      if (errData?.message) {
        const toast = `Refresh failed \u2014 ${errData.message}${errData.nextStep ? ". " + errData.nextStep : ""}`;
        message.error(toast);
      } else {
        message.error("Refresh failed");
      }
    }
  };

  const handleArchive = async (record) => {
    try {
      await archiveDocument(record.id);
      message.success(record.name + " archived");
      onRefresh && onRefresh();
    } catch (err) {
      message.error("Archive failed");
    }
  };

  const handleRestore = async (record) => {
    try {
      await restoreDocument(record.id);
      message.success(record.name + " restored");
      onRefresh && onRefresh();
    } catch (err) {
      message.error("Restore failed");
    }
  };

  const columns = [
    {
      title: "Document",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (name, record) => (
        <div>
          <div className="doc-mgmt-doc-name" onClick={() => setDetailDoc(record)}>
            {name}
          </div>
          {record.url && (record.type === "web" || record.type === "video") && (
            <div className="doc-mgmt-doc-url">
              <UrlLink url={record.url} />
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 90,
      render: (type) => (
        <Tag color={TYPE_COLORS[type]}>{TYPE_LABELS[type] || type}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status) => (
        <Tag color={status === "active" ? "green" : "default"}>
          {status === "active" ? "Active" : "Archived"}
        </Tag>
      ),
    },
    {
      title: "Chunks",
      dataIndex: "chunks",
      key: "chunks",
      width: 85,
      align: "center",
    },
    {
      title: "Last Ingested",
      dataIndex: "lastIngested",
      key: "lastIngested",
      width: 130,
      render: (date) => (
        <span style={{ color: "#888" }}>
          {date ? moment(date).format("MMM D, YYYY") : "-"}
        </span>
      ),
    },
    {
      title: "Hit Rate",
      dataIndex: "hitRate",
      key: "hitRate",
      width: 100,
      render: (rate) => <HitRateBar rate={rate} />,
    },
    {
      title: "",
      key: "actions",
      width: 44,
      render: (_, record) => {
        const isRefreshable = record.type === "web" || record.type === "video";
        const menu = (
          <Menu>
            <Menu.Item key="details" onClick={() => setDetailDoc(record)}>
              View details
            </Menu.Item>
            {isRefreshable && (
              <Menu.Item key="refresh" onClick={() => handleRefresh(record)}>
                Refresh now
              </Menu.Item>
            )}
            <Menu.Divider />
            {record.status === "archived" ? (
              <Menu.Item key="restore" onClick={() => handleRestore(record)}>
                Restore
              </Menu.Item>
            ) : (
              <Menu.Item key="archive" onClick={() => handleArchive(record)}>
                Archive
              </Menu.Item>
            )}
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <EllipsisOutlined
              rotate={90}
              style={{ fontSize: 18, cursor: "pointer", color: "#888" }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={documents}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          onChange: onPageChange,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: null,
        }}
      />
      <DetailModal
        visible={!!detailDoc}
        doc={detailDoc}
        onClose={() => setDetailDoc(null)}
        onRefresh={onRefresh}
      />
    </>
  );
}
